// 組織樹的錯誤文案（SPEC #org-tree-errors）：有 detail.reason 的照原因寫，其他沿用 org.err_*。
import { isKinioError } from '@kinio/web-sdk';
import { errText } from '../settings/OrgSection';

type T = (k: string, o?: Record<string, unknown>) => string;

export function reasonOf(e: unknown): string {
  return isKinioError(e) ? String(e.detail?.reason ?? '') : '';
}

export function isPreviewStale(e: unknown): boolean {
  return isKinioError(e) && e.status === 409 && e.code === 'VERSION_CONFLICT' && reasonOf(e) === 'preview_stale';
}

export function retryAfter(e: unknown): number {
  if (!isKinioError(e) || e.code !== 'RATE_LIMITED') return 0;
  const n = Number(e.detail?.retry_after_seconds ?? 0);
  return Number.isFinite(n) && n > 0 ? Math.ceil(n) : 30;
}

export function otErr(t: T, e: unknown): string {
  if (!isKinioError(e)) return errText(t, e);
  const reason = reasonOf(e);
  const d = e.detail ?? {};
  switch (e.code) {
    case 'RATE_LIMITED':
      return t('ot.err_rate_limited', { count: retryAfter(e) });
    case 'TREE_DEPTH_EXCEEDED':
      return t('ot.err_depth', { n: Number(d.max_depth ?? 8) });
    case 'TREE_LIMIT':
      return t('ot.err_limit', { n: Number(d.max ?? 0) || '' });
    case 'TREE_BUSY':
      return t('ot.err_busy');
    case 'NODE_NOT_EMPTY':
      return t('ot.err_not_empty', { homes: (d.homes as unknown[] | undefined)?.length ?? 0, nodes: (d.nodes as unknown[] | undefined)?.length ?? 0 });
    case 'TEAM_ORG_REQUIRED':
      return t('ot.err_team_org');
    case 'USE_NODE_ASSIGNMENT':
      return t('ot.err_use_node_assignment');
    case 'DELEGATION_REQUEST_GONE':
      return t('ot.err_code_gone');
    case 'DELEGATION_SCOPE_EXCEEDED':
      return t('ot.err_scope_exceeded');
    case 'SITE_CYCLE':
      return t('ot.err_cycle');
    case 'OUT_OF_ANCHOR':
      return t('ot.err_out_of_anchor');
    case 'GRANT_EXCEEDS_ROLE':
      return t('ot.err_exceeds_' + String(d.field ?? 'actions'), { defaultValue: t('ot.err_exceeds_actions') });
    case 'VERSION_CONFLICT':
      return reason === 'preview_stale' ? t('ot.err_preview_stale') : errText(t, e);
    case 'CONFLICT':
      return reason === 'home_assigned' ? t('ot.err_home_assigned') : t('ot.err_conflict');
    case 'INVALID_ARGUMENT':
      if (d.field === 'history_from') return t('ot.err_history_max', { count: Number(d.max_days ?? 30) });
      if (d.field === 'node') return t('ot.err_root_node');
      if (d.field === 'reason') return t('ot.err_high_risk_reason');
      return t('ot.err_invalid');
    case 'FORBIDDEN':
      if (reason) return t('ot.err_forbidden_' + reason, { defaultValue: errText(t, e) });
      return errText(t, e);
    case 'ORG_MFA_REQUIRED':
      if (reason === 'delegator_mfa_required') return t('ot.mfa_needed', { from: String(d.from_org_name ?? '') });
      return errText(t, e);
  }
  return errText(t, e);
}
